import { Helmet } from 'react-helmet-async'

const SITE_NAME = 'Fravai'
const SITE_URL = 'https://fravai.com'
const DEFAULT_TITLE = 'Fravai — Генерация карточек товара и инфографики'
const DEFAULT_DESCRIPTION =
  'Fravai — AI-студия, которая помогает брендам, селлерам маркетплейсов и реселлерам генерировать карточки товаров, промо-баннеры и примерку одежды за минуты.'
export const DEFAULT_OG_IMAGE = 'https://storage.yandexcloud.net/ai-previews/social-card.png?v=2'

type MetaImage = {
  url: string
  alt?: string
  width?: number
  height?: number
}

type JsonLdInput = Record<string, unknown> | Array<Record<string, unknown>>

type SeoProps = {
  title?: string
  description?: string
  canonical?: string
  keywords?: string[]
  robots?: string
  noindex?: boolean
  openGraph?: {
    type?: string
    image?: MetaImage
  }
  twitter?: {
    card?: 'summary' | 'summary_large_image' | 'app' | 'player'
    creator?: string
  }
  jsonLd?: JsonLdInput
}

function resolveToAbsoluteUrl(path?: string) {
  if (!path) return SITE_URL
  try {
    return new URL(path, SITE_URL).toString()
  } catch {
    return SITE_URL
  }
}

export function Seo({
  title,
  description,
  canonical,
  keywords,
  robots,
  noindex,
  openGraph,
  twitter,
  jsonLd,
}: SeoProps) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE
  const metaDescription = description ?? DEFAULT_DESCRIPTION
  const canonicalUrl = resolveToAbsoluteUrl(canonical)
  const robotsContent = noindex ? 'noindex, nofollow' : robots ?? 'index, follow'
  const ogType = openGraph?.type ?? 'website'
  const ogImage = openGraph?.image ?? { url: DEFAULT_OG_IMAGE, alt: `${SITE_NAME} — AI-контент для маркетплейсов` }
  const twitterCard = twitter?.card ?? 'summary_large_image'

  const jsonLdArray = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : []

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <link rel="canonical" href={canonicalUrl} />
      <meta name="description" content={metaDescription} />
      <meta name="robots" content={robotsContent} />
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="ru_RU" />
      <meta property="og:image" content={ogImage.url} />
      {ogImage.alt && <meta property="og:image:alt" content={ogImage.alt} />}
      {ogImage.width && <meta property="og:image:width" content={String(ogImage.width)} />}
      {ogImage.height && <meta property="og:image:height" content={String(ogImage.height)} />}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={ogImage.url} />
      {twitter?.creator && <meta name="twitter:creator" content={twitter.creator} />}
      {keywords?.length ? <meta name="keywords" content={keywords.join(', ')} /> : null}
      {jsonLdArray.map((schema, index) => (
        <script
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ))}
    </Helmet>
  )
}
